<template>
  <DeepDialog
    v-model="showDialog"
    :closable="!isDeepIdWebOpen"
    data-test-id="authentication-dialog"
    fullscreen-on-mobile
    :persistent="isDeepIdWebOpen"
    width="600"
    @click:outside="!isDeepIdWebOpen && closeDialog()"
    @close="closeDialog"
    @keydown.esc="isDeepIdWebOpen ? confirmCloseDeepIdWeb() : closeDialog()"
  >
    <template #card-title-content-title>
      <portal-target name="identify-dialog-title"></portal-target>
    </template>
    <template v-if="isDeepIdWebOpen" #card-title-content-actions-append>
      <v-btn
        icon="$close"
        size="small"
        variant="plain"
        @click="confirmCloseDeepIdWeb"
      />
    </template>
    <template #content>
      <portal-target name="identify-dialog-subtitle"> </portal-target>
      <!-- SMS CODE -->
      <SmsCodeIdentifyCard
        v-if="
          isSmsCode &&
          !deepAdminInternalStore.isVerified &&
          !showWarning &&
          !showMobileIDWarning
        "
        @cancel="closeDialog"
        @done="closeDialog"
        @show-warning="showWarning = true"
      />
      <!-- /SMS CODE -->
      <!-- DEEP ID -->
      <DeepIDIdentifyCard
        v-if="
          isDeepID &&
          !deepAdminInternalStore.isDeepIDVerified &&
          !showWarning &&
          !showMobileIDWarning
        "
        @close-dialog="closeDialog"
        @web-ident-change="isDeepIdWebOpen = $event"
      />
      <!-- /DEEP ID -->

      <!-- MOBILE ID -->
      <MobileIDIdentifyCard
        v-if="isMobileID && !showWarning && !showMobileIDWarning"
        @cancel="closeDialog"
        @done="handleDone"
        @show-mobile-id-warning="showMobileIDWarning = true"
      />
      <!-- /MOBILE ID -->

      <!-- VERIFIED -->
      <SuccessfulVerifiedCard
        v-if="
          ((isMobileID || isSmsCode) && deepAdminInternalStore.isRasVerified) ||
          (isDeepID &&
            deepAdminInternalStore.isDeepIDVerification &&
            deepAdminInternalStore.isDeepIDVerified)
        "
        :authority-service="authorityServiceKey"
        @close-dialog="closeDialog"
      />
      <!-- /VERIFIED -->

      <!-- WARNING -->
      <IdentifyWarningCard
        v-if="showWarning && !showMobileIDWarning"
        :authority-service="authorityServiceKey"
        @close-dialog="closeDialog"
      />
      <!-- /WARNING -->

      <!-- MOBILE ID WARNING -->
      <MobileIDWarningCard
        v-if="showMobileIDWarning"
        @cancel="closeDialog"
        @close-dialog="closeDialog"
      />
      <!-- /MOBILE ID WARNING -->
    </template>
    <template #actions>
      <portal-target name="identify-dialog-actions"></portal-target>
    </template>
  </DeepDialog>
</template>

<script lang="ts" setup>
import SuccessfulVerifiedCard from '@/components/cards/SuccessfulVerifiedCard.vue';
import SmsCodeIdentifyCard from '@/components/cards/SmsCodeIdentifyCard.vue';
import MobileIDWarningCard from '@/components/cards/MobileIDWarningCard.vue';
import MobileIDIdentifyCard from '@/components/cards/MobileIDIdentifyCard.vue';
import IdentifyWarningCard from '@/components/cards/IdentifyWarningCard.vue';
import DeepIDIdentifyCard from '@/components/cards/DeepIDIdentifyCard.vue';
import AuthService from '@/services/authService.ts';
import { useMessageStore } from '@/stores/message.ts';
import { buildUserValidationPayload } from '@/utils/userValidationPayload.ts';
import { SignatureMode } from '@/types/enums/SignatureMode.ts';
import { AuthorityService } from '@/types/enums/AuthorityService.ts';
import { useDeepAdminInternalStore } from '@/stores/deepadmin/deepadmin-internal.ts';
import { computed, onUnmounted, provide, ref } from 'vue';
import { useEmitter } from '@/composables/useEmitter';
import { useDeepIdWebCancelConfirm } from '@/composables/useDeepIdWebCancelConfirm';
import { DeepDialog } from '@deepcloud/deep-ui-lib';
import { PortalTarget } from 'portal-vue';
import { useDeepAdminStore } from '@/stores/deepadmin/deepadmin';
import {
  type QrGenerationStatus,
  qrGenerationStatusKey
} from '@/types/QrGenerationStatusKey';

const messageStore = useMessageStore();
const deepAdminInternalStore = useDeepAdminInternalStore();
const deepAdminStore = useDeepAdminStore();
const emitter = useEmitter();
const showDialog = ref(false);
const showWarning = ref(false);
const showMobileIDWarning = ref(false);
const authService = ref(null);
const onboardingTimeout = ref();
const userId = ref(deepAdminStore.userInfo?.id);
const signMode = ref(null);

const qrGenerationStatus = ref<QrGenerationStatus>('idle');

provide(qrGenerationStatusKey, qrGenerationStatus);

const isSmsCode = computed(() => {
  return (
    authorityServiceKey.value === AuthorityService.MOBILE_ID &&
    signMode.value === SignatureMode.ADVANCED
  );
});

const isMobileID = computed(() => {
  return (
    authorityServiceKey.value === AuthorityService.MOBILE_ID &&
    signMode.value === SignatureMode.QUALIFIED
  );
});

const isDeepID = computed(() => {
  return authorityServiceKey.value === AuthorityService.DEEP_ID;
});

const authorityServiceKey = computed(() => {
  return authService.value?.key || '';
});

emitter.$on('restart-verification', restartVerification);
emitter.$on('open-identify-dialog', openDialog);

onUnmounted(() => {
  emitter.$off('open-identify-dialog', openDialog);
  emitter.$off('restart-verification', restartVerification);
});

function openDialog({ authorityService, signatureMode }) {
  authService.value = authorityService;
  signMode.value = signatureMode;
  if (authorityServiceKey.value === AuthorityService.DEEP_ID) {
    checkStatus();
  }
  showDialog.value = true;
}

function handleDone() {
  deepAdminInternalStore.isRasVerified = true;
  closeDialog();
}

const isDeepIdWebOpen = ref(false);
const { confirmCancel } = useDeepIdWebCancelConfirm();

async function confirmCloseDeepIdWeb() {
  if (await confirmCancel()) closeDialog();
}

function closeDialog() {
  showDialog.value = false;
  showWarning.value = false;
  showMobileIDWarning.value = false;
  setTimeout(() => {
    authService.value = null;
    if (onboardingTimeout.value) {
      clearTimeout(onboardingTimeout.value);
    }
  }, 200);
}

async function checkStatus() {
  if (userId.value) {
    try {
      await deepAdminInternalStore
        .fetchUserVerification(userId.value)
        .then(() => {
          qrGenerationStatus.value = 'verifying';

          if (deepAdminInternalStore.userValidationURL) {
            qrGenerationStatus.value = 'success';
          }
        })
        .catch(() => {
          qrGenerationStatus.value = 'error';
        });
      if (
        deepAdminInternalStore.isVerified &&
        deepAdminInternalStore.isDeepIDVerification &&
        deepAdminInternalStore.isDeepIDVerified
      ) {
        emitter.$emit('enable-deep-id');
      }
      if (
        deepAdminInternalStore.shouldStartValidation &&
        AuthService.user &&
        userId.value
      ) {
        const payload = buildUserValidationPayload(AuthService.user);

        await deepAdminInternalStore
          .startUserVerification(userId.value, payload)
          .then(() => {
            checkStatus();
          });
      } else if (deepAdminInternalStore.isInProgress) {
        onboardingTimeout.value = setTimeout(checkStatus, 3000);
      } else {
        clearTimeout(onboardingTimeout.value);
      }
    } catch (error) {
      messageStore.showMessage({
        key: 'errors.custom.identification_status',
        color: 'error',
        icon: 'fa fa-circle-exclamation'
      });
    }
  }
}

async function restartVerification() {
  if (AuthService.user && userId.value) {
    clearTimeout(onboardingTimeout.value);
    const payload = buildUserValidationPayload(AuthService.user);
    qrGenerationStatus.value = 'verifying';
    await deepAdminInternalStore
      .startUserVerification(userId.value, payload)
      .then((e) => {
        const regeneratedData = e.data;
        if (deepAdminInternalStore.onboardingStatus) {
          deepAdminInternalStore.onboardingStatus.items =
            deepAdminInternalStore.onboardingStatus?.items.map((item) => {
              // Replace Deep ID one
              if (item.source === regeneratedData.source) {
                return {
                  ...item,
                  voucher: regeneratedData.voucher,
                  url: regeneratedData.url
                };
              }
              return item; // Return the unchanged item
            });
        }
      })
      .catch(() => {
        qrGenerationStatus.value = 'error';
      })
      .finally(() => {
        qrGenerationStatus.value = 'success';
        checkStatus();
      });
  }
}
</script>
